/**
 * useWorkflowConfig - Hook for fetching workflow configuration
 * Uses new V2 types: CompleteWorkflow, WorkflowStatus, WorkflowTransition
 */

import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  CompleteWorkflow,
  WorkflowStatus,
  WorkflowTransition,
  UnifiedCaseType,
  UnifiedBookingStatus,
} from '@leal-crm/shared';

/**
 * Fetch all published workflows
 */
export function useWorkflows() {
  return useQuery({
    queryKey: ['workflows'],
    queryFn: async () => {
      return api.get<CompleteWorkflow[]>('/api/workflows');
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch published workflow for specific booking type
 */
export function useWorkflowByType(bookingType: UnifiedCaseType | undefined) {
  return useQuery({
    queryKey: ['workflows', 'booking', bookingType],
    queryFn: async () => {
      if (!bookingType) return null;
      return api.get<CompleteWorkflow>(`/api/workflows/booking/${bookingType}`);
    },
    enabled: !!bookingType,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch published job workflow for specific booking type
 */
export function useJobWorkflowByType(bookingType: UnifiedCaseType | undefined) {
  return useQuery({
    queryKey: ['workflows', 'job', bookingType],
    queryFn: async () => {
      if (!bookingType) return null;
      return api.get<CompleteWorkflow>(`/api/workflows/job/${bookingType}`);
    },
    enabled: !!bookingType,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get status config from workflow by status code
 */
export function getStatusConfig(
  workflow: CompleteWorkflow | null | undefined,
  statusCode: UnifiedBookingStatus
): WorkflowStatus | undefined {
  return workflow?.statuses.find((s) => s.code === statusCode);
}

/**
 * Check if a transition is enabled in the workflow
 * Purely configuration-driven - no fallbacks.
 * Returns false if no workflow or transition not enabled.
 */
export function isWorkflowTransitionEnabled(
  workflow: CompleteWorkflow | null | undefined,
  fromStatus: UnifiedBookingStatus,
  toStatus: UnifiedBookingStatus
): boolean {
  // No workflow = no transitions allowed
  if (!workflow) return false;

  // Find status IDs
  const fromStatusObj = workflow.statuses.find((s) => s.code === fromStatus);
  const toStatusObj = workflow.statuses.find((s) => s.code === toStatus);

  // If statuses don't exist in workflow, disallow
  if (!fromStatusObj || !toStatusObj) return false;

  const transition = workflow.transitions.find(
    (t) => t.fromStatusId === fromStatusObj.id && t.toStatusId === toStatusObj.id
  );

  // Only allow if transition exists AND is enabled
  return transition?.isEnabled ?? false;
}

/**
 * Check if a status is active in the workflow
 */
export function isStatusActive(
  workflow: CompleteWorkflow | null | undefined,
  statusCode: UnifiedBookingStatus
): boolean {
  // If no workflow, default to active (will use static rules)
  if (!workflow) return true;

  const status = workflow.statuses.find((s) => s.code === statusCode);
  // If status not found in workflow, it's not active for this workflow
  if (!status) return false;
  return status.isActive;
}

/**
 * Get allowed transitions for a status based on workflow config
 */
export function getWorkflowAllowedTransitions(
  workflow: CompleteWorkflow | null | undefined,
  fromStatus: UnifiedBookingStatus
): UnifiedBookingStatus[] {
  if (!workflow) return [];

  const fromStatusObj = workflow.statuses.find((s) => s.code === fromStatus);
  if (!fromStatusObj) return [];

  return workflow.transitions
    .filter((t) => t.fromStatusId === fromStatusObj.id && t.isEnabled)
    .map((t) => {
      const toStatus = workflow.statuses.find((s) => s.id === t.toStatusId);
      return toStatus?.code as UnifiedBookingStatus;
    })
    .filter((code): code is UnifiedBookingStatus => !!code && isStatusActive(workflow, code));
}

/**
 * Get display label for a status from workflow config
 */
export function getStatusLabel(
  workflow: CompleteWorkflow | null | undefined,
  statusCode: UnifiedBookingStatus,
  defaultLabel: string
): string {
  if (!workflow) return defaultLabel;

  const status = workflow.statuses.find((s) => s.code === statusCode);
  return status?.name || defaultLabel;
}

/**
 * Status color configuration interface
 */
export interface StatusColors {
  text: string;
  bg: string;
  border: string;
}

const FALLBACK_STATUS_COLORS: StatusColors = {
  text: '#6B7280',
  bg: '#F3F4F6',
  border: '#E5E7EB',
};

/**
 * Get status colors from workflow config
 * Falls back to default colors if not configured
 */
export function getStatusColors(
  workflow: CompleteWorkflow | null | undefined,
  statusCode: UnifiedBookingStatus
): StatusColors {
  if (!workflow) {
    return FALLBACK_STATUS_COLORS;
  }

  const status = workflow.statuses.find((s) => s.code === statusCode);
  if (!status) {
    return FALLBACK_STATUS_COLORS;
  }

  return {
    text: status.color || FALLBACK_STATUS_COLORS.text,
    bg: status.bgColor || FALLBACK_STATUS_COLORS.bg,
    border: status.borderColor || FALLBACK_STATUS_COLORS.border,
  };
}

/**
 * Get inline style object for a status badge/tag
 */
export function getStatusStyle(
  workflow: CompleteWorkflow | null | undefined,
  statusCode: UnifiedBookingStatus
): React.CSSProperties {
  const colors = getStatusColors(workflow, statusCode);
  return {
    color: colors.text,
    backgroundColor: colors.bg,
    borderColor: colors.border,
  };
}

/**
 * Get all status configs as a map for quick lookup
 */
export function getStatusConfigMap(
  workflow: CompleteWorkflow | null | undefined
): Map<string, { label: string; colors: StatusColors; isActive: boolean; showInJobboard: boolean }> {
  const map = new Map();

  if (!workflow) {
    return map;
  }

  workflow.statuses.forEach((status) => {
    map.set(status.code, {
      label: status.name,
      colors: {
        text: status.color || FALLBACK_STATUS_COLORS.text,
        bg: status.bgColor || FALLBACK_STATUS_COLORS.bg,
        border: status.borderColor || FALLBACK_STATUS_COLORS.border,
      },
      isActive: status.isActive,
      showInJobboard: status.showInJobboard,
    });
  });

  return map;
}

/**
 * Get transition config between two statuses
 */
export function getTransitionConfig(
  workflow: CompleteWorkflow | null | undefined,
  fromStatus: UnifiedBookingStatus,
  toStatus: UnifiedBookingStatus
): WorkflowTransition | undefined {
  if (!workflow) return undefined;

  const fromStatusObj = workflow.statuses.find((s) => s.code === fromStatus);
  const toStatusObj = workflow.statuses.find((s) => s.code === toStatus);

  if (!fromStatusObj || !toStatusObj) return undefined;

  return workflow.transitions.find(
    (t) => t.fromStatusId === fromStatusObj.id && t.toStatusId === toStatusObj.id
  );
}

/**
 * Get dialog type for a transition from workflow config
 */
export function getTransitionDialogType(
  workflow: CompleteWorkflow | null | undefined,
  fromStatus: UnifiedBookingStatus,
  toStatus: UnifiedBookingStatus
): string {
  const transition = getTransitionConfig(workflow, fromStatus, toStatus);
  return transition?.dialogType || 'confirm';
}
